import { formatCnpj, organizationInputSchema, type OrganizationInput } from '@fluxus/shared';
import { useId, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ApiError, errorMessage } from '@/lib/api/errors';
import { apiFieldErrors, hasFieldErrors, zodFieldErrors, type FieldErrors } from '@/lib/forms';

type Props = {
  initial?: { name: string; legalName: string | null; document: string | null };
  submitLabel: string;
  disabled?: boolean;
  onSubmit: (input: OrganizationInput) => Promise<unknown>;
  onCancel?: () => void;
};

/** Cadastro da empresa cliente (criar e editar): nome, razão social e CNPJ. */
export function OrganizationForm({ initial, submitLabel, disabled, onSubmit, onCancel }: Props) {
  const [name, setName] = useState(initial?.name ?? '');
  const [legalName, setLegalName] = useState(initial?.legalName ?? '');
  const [document, setDocument] = useState(initial?.document ? formatCnpj(initial.document) : '');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const ids = { name: useId(), legalName: useId(), document: useId() };

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    const parsed = organizationInputSchema.safeParse({ name, legalName, document });
    if (!parsed.success) {
      setErrors(zodFieldErrors(parsed.error));
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      // Envia o que o schema normalizou (CNPJ só dígitos, vazio = nulo): o mesmo que a API grava.
      await onSubmit(parsed.data);
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (err instanceof ApiError && err.code === 'DOCUMENT_TAKEN') fields.document = err.message;
      if (hasFieldErrors(fields)) setErrors(fields);
      else setFormError(err);
    } finally {
      setSaving(false);
    }
  }

  const clear = (field: string) => setErrors((e) => ({ ...e, [field]: undefined }));

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-5">
      <FormField id={ids.name} label="Nome da empresa" error={errors.name}>
        <Input
          {...fieldA11y(ids.name, errors.name)}
          value={name}
          maxLength={120}
          disabled={disabled}
          onChange={(e) => {
            setName(e.target.value);
            clear('name');
          }}
          className="h-11"
        />
      </FormField>
      <FormField id={ids.legalName} label="Razão social (opcional)" error={errors.legalName}>
        <Input
          {...fieldA11y(ids.legalName, errors.legalName)}
          value={legalName}
          maxLength={200}
          disabled={disabled}
          onChange={(e) => {
            setLegalName(e.target.value);
            clear('legalName');
          }}
          className="h-11"
        />
      </FormField>
      <FormField id={ids.document} label="CNPJ (opcional)" error={errors.document}>
        <Input
          {...fieldA11y(ids.document, errors.document)}
          value={document}
          inputMode="numeric"
          placeholder="00.000.000/0000-00"
          maxLength={18}
          disabled={disabled}
          onChange={(e) => {
            setDocument(e.target.value);
            clear('document');
          }}
          onBlur={() => {
            const digits = document.replace(/\D/g, '');
            if (digits.length === 14) setDocument(formatCnpj(digits));
          }}
          className="h-11"
        />
      </FormField>
      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      {disabled ? null : (
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {onCancel ? (
            <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
              Cancelar
            </Button>
          ) : null}
          <Button type="submit" loading={saving}>
            {submitLabel}
          </Button>
        </div>
      )}
    </form>
  );
}
